// Decodes a file with the browser's own decoder (Web Audio).
export interface Decoded {
  buffer: AudioBuffer;
  /** True when we decoded at a reduced rate for display only; playback then uses <audio>. */
  previewOnly: boolean;
}

/** Long files decode to a lot of memory at full rate (1 h stereo ≈ 1.3 GB), so we reduce the rate. */
const FULL_RATE_LIMIT_SEC = 20 * 60;

export async function decode(bytes: Uint8Array, estDuration: number | undefined, sampleRate?: number): Promise<Decoded> {
  const copy = bytes.slice().buffer;
  if (estDuration && estDuration > FULL_RATE_LIMIT_SEC) {
    const ctx = new OfflineAudioContext(1, 1, 8000);
    return { buffer: await ctx.decodeAudioData(copy), previewOnly: true };
  }
  // Decode at the file's own rate so the spectrogram shows its true frequency range.
  const rate = sampleRate && sampleRate >= 8000 && sampleRate <= 384000 ? sampleRate : 48000;
  const ctx = new OfflineAudioContext(1, 1, rate);
  const buffer = await ctx.decodeAudioData(copy);
  return { buffer, previewOnly: false };
}

/**
 * Decodes raw PCM ourselves, for layouts the browser won't (AIFF, big-endian, odd bit depths).
 * 8-bit WAV is unsigned; 8-bit AIFF is signed.
 */
export function decodePcm(bytes: Uint8Array, p: import('../parsers/types').PcmLayout, unsigned8: boolean): Decoded {
  const bps = p.bitsPerSample;
  const bytesPer = Math.ceil(bps / 8);
  const frames = Math.floor((p.dataEnd - p.dataStart) / p.blockAlign);
  const ctx = new OfflineAudioContext(1, 1, Math.max(8000, Math.min(384000, p.sampleRate)));
  const buf = ctx.createBuffer(p.channels, Math.max(1, frames), p.sampleRate);
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const le = !p.bigEndian;
  const chans = Array.from({ length: p.channels }, (_, c) => buf.getChannelData(c));
  for (let i = 0; i < frames; i++) {
    const base = p.dataStart + i * p.blockAlign;
    for (let c = 0; c < p.channels; c++) {
      const o = base + c * bytesPer;
      let v: number;
      if (p.float) v = bytesPer === 8 ? dv.getFloat64(o, le) : dv.getFloat32(o, le);
      else if (bytesPer === 1) v = unsigned8 ? (bytes[o] - 128) / 128 : dv.getInt8(o) / 128;
      else if (bytesPer === 2) v = dv.getInt16(o, le) / 32768;
      else if (bytesPer === 3) {
        const b0 = bytes[o], b1 = bytes[o + 1], b2 = bytes[o + 2];
        const n = le ? b0 | (b1 << 8) | (b2 << 16) : b2 | (b1 << 8) | (b0 << 16);
        v = ((n << 8) >> 8) / 8388608;
      } else v = dv.getInt32(o, le) / 2147483648;
      chans[c][i] = v;
    }
  }
  return { buffer: buf, previewOnly: false };
}
